import { useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MAX_SUMMARY_FIELDS = 6;

type FlowReply = {
  name?: string;
  body?: string;
  responseJson?: unknown;
  responseJsonRaw?: string;
};

/** Flatten a flow response object into displayable key/value pairs (first level only). */
function summarizeResponse(responseJson: unknown): Array<{ key: string; value: string }> {
  if (!responseJson || typeof responseJson !== 'object' || Array.isArray(responseJson)) return [];
  return Object.entries(responseJson as Record<string, unknown>)
    .filter(([key]) => key !== 'flow_token')
    .map(([key, value]) => ({
      key,
      value: typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
    }))
    .filter((entry) => entry.value !== '');
}

/** Renders an incoming WhatsApp Flow completion (nfm_reply) as a compact form summary. */
export function FlowReplyCard({ nfmReply }: { nfmReply: FlowReply }) {
  const { t } = useTranslation('conversations');
  const [expanded, setExpanded] = useState(false);

  const fields = summarizeResponse(nfmReply.responseJson);
  const visibleFields = expanded ? fields : fields.slice(0, MAX_SUMMARY_FIELDS);
  const hasMore = fields.length > MAX_SUMMARY_FIELDS;

  return (
    <div className="rounded-md bg-chat-bubble-overlay border border-chat-border-soft px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-sm font-medium text-chat-accent-deep">
        <ClipboardCheck className="h-3.5 w-3.5" />
        {nfmReply.name || t('messageView.flowReply.title')}
      </div>
      {visibleFields.length > 0 && (
        <dl className="mt-1.5 space-y-0.5">
          {visibleFields.map((field) => (
            <div key={field.key} className="flex gap-1.5 text-xs">
              <dt className="text-chat-text-muted shrink-0">{field.key}:</dt>
              <dd className="text-chat-text break-all">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {fields.length === 0 && nfmReply.responseJsonRaw && (
        <p className="mt-1 text-xs text-chat-text-muted break-all">{nfmReply.responseJsonRaw}</p>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="mt-1 flex items-center gap-1 text-xs text-chat-link hover:underline"
        >
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? t('messageView.flowReply.showLess') : t('messageView.flowReply.showMore')}
        </button>
      )}
    </div>
  );
}
